/*
 * <product-set> — bloque de conjunto (top + bottom) en la página de producto.
 * Permite elegir talla de cada pieza y agregar las dos al carrito en un solo clic.
 */
if (!customElements.get('product-set')) {
  customElements.define(
    'product-set',
    class ProductSet extends HTMLElement {
      constructor() {
        super();

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.labelReady = this.dataset.labelReady || 'Agregar el conjunto';
        this.labelIncomplete = this.dataset.labelIncomplete || 'Elige las tallas';
        this.labelError = this.dataset.labelError || 'Elige la talla de cada pieza.';
        this.labelGenericError = this.dataset.labelGenericError || 'No pudimos agregar el conjunto. Intenta de nuevo.';

        this.items = Array.from(this.querySelectorAll('[data-product-set-item]'));
        this.submitButton = this.querySelector('[data-product-set-submit]');
        this.totalTarget = this.querySelector('[data-product-set-total]');
        this.errorTarget = this.querySelector('[data-product-set-error]');
      }

      connectedCallback() {
        if (!this.items.length || !this.submitButton) return;

        this.items.forEach((item) => {
          item.querySelectorAll('[data-variant-id]').forEach((option) => {
            option.addEventListener('click', () => this.select(item, option));
          });
        });

        this.submitButton.addEventListener('click', () => this.onSubmit());
        this.syncWithMainPicker();
        this.refresh();
      }

      /* Si la clienta cambia la talla arriba, la primera fila del conjunto la sigue. */
      syncWithMainPicker() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        const input = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
        const firstItem = this.items[0];
        if (!input || !firstItem || firstItem.dataset.followsMainPicker !== 'true') return;

        input.addEventListener('change', () => {
          const option = firstItem.querySelector(`[data-variant-id="${input.value}"]`);
          if (option && !option.hasAttribute('disabled')) this.select(firstItem, option);
        });
      }

      select(item, option) {
        if (option.hasAttribute('disabled')) return;

        item.querySelectorAll('[data-variant-id]').forEach((other) => {
          other.setAttribute('aria-pressed', other === option ? 'true' : 'false');
        });

        this.hideError();
        this.refresh();
      }

      selectionFor(item) {
        return item.querySelector('[data-variant-id][aria-pressed="true"]');
      }

      refresh() {
        let total = 0;
        let complete = true;

        this.items.forEach((item) => {
          const selected = this.selectionFor(item);
          if (selected) {
            total += parseInt(selected.dataset.price, 10) || 0;
          } else {
            complete = false;
            total += parseInt(item.dataset.fallbackPrice, 10) || 0;
          }
        });

        if (this.totalTarget) this.totalTarget.textContent = this.formatMoney(total);

        this.submitButton.classList.toggle('is-ready', complete);
        this.submitButton.textContent = complete ? this.labelReady : this.labelIncomplete;
      }

      onSubmit() {
        const selections = this.items.map((item) => this.selectionFor(item));

        if (selections.some((selection) => !selection)) {
          this.showError(this.labelError);
          return;
        }

        const items = selections.map((selection) => ({
          id: parseInt(selection.dataset.variantId, 10),
          quantity: 1,
        }));

        this.addToCart(items);
      }

      addToCart(items) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items: items };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',');
          body.sections_url = window.location.pathname;
        }

        this.submitButton.setAttribute('aria-busy', 'true');
        this.hideError();

        fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || this.labelGenericError);
              return;
            }

            if (cart && typeof cart.renderContents === 'function' && response.sections) {
              const added = response.items && response.items.length ? response.items[0] : null;
              cart.renderContents({
                key: added ? added.key : null,
                sections: response.sections,
              });

              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-set' });
              }
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch(() => this.showError(this.labelGenericError))
          .finally(() => this.submitButton.removeAttribute('aria-busy'));
      }

      showError(message) {
        if (!this.errorTarget) return;
        this.errorTarget.textContent = message;
        this.errorTarget.classList.add('is-visible');
      }

      hideError() {
        if (!this.errorTarget) return;
        this.errorTarget.classList.remove('is-visible');
      }

      /* Réplica ligera de Shopify.formatMoney para el total del conjunto. */
      formatMoney(cents) {
        const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;

        const withDelimiters = (number, precision, thousands, decimal) => {
          if (isNaN(number) || number === null) return 0;
          const fixed = (number / 100.0).toFixed(precision);
          const parts = fixed.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const cents = parts[1] ? decimal + parts[1] : '';
          return dollars + cents;
        };

        let formatted = '';
        const match = this.moneyFormat.match(placeholder);
        const type = match ? match[1] : 'amount';

        switch (type) {
          case 'amount':
            formatted = withDelimiters(value, 2, ',', '.');
            break;
          case 'amount_no_decimals':
            formatted = withDelimiters(value, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            formatted = withDelimiters(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            formatted = withDelimiters(value, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            formatted = withDelimiters(value, 2, "'", '.');
            break;
          case 'amount_with_space_separator':
            formatted = withDelimiters(value, 2, ' ', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            formatted = withDelimiters(value, 0, ' ', ',');
            break;
          default:
            formatted = withDelimiters(value, 2, ',', '.');
        }

        return this.moneyFormat.replace(placeholder, formatted);
      }
    }
  );
}
